/**
 * Test Check-in Manager
 * Allows coaches to create backdated check-ins for test clients
 * to test various adjustment scenarios without waiting weeks.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Save, RefreshCw, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preset scenarios for quick setup
const PRESETS = {
  plateau: {
    name: "Plateau (2-3 weeks flat)",
    description: "High adherence but weight not moving",
    checkins: [
      { weight: 80.0, adherence: 92, weekend: 88, hunger: 8, energy: 8, stress: 7 },
      { weight: 80.1, adherence: 90, weekend: 85, hunger: 7, energy: 8, stress: 8 },
      { weight: 80.0, adherence: 91, weekend: 87, hunger: 8, energy: 7, stress: 8 },
      { weight: 80.0, adherence: 93, weekend: 90, hunger: 8, energy: 8, stress: 7 },
    ]
  },
  losing_too_fast: {
    name: "Losing Too Fast (>1%/week)",
    description: "Good adherence but losing too rapidly",
    checkins: [
      { weight: 82.0, adherence: 95, weekend: 92, hunger: 8, energy: 9, stress: 8 },
      { weight: 80.5, adherence: 94, weekend: 90, hunger: 8, energy: 8, stress: 8 },
      { weight: 78.8, adherence: 96, weekend: 93, hunger: 7, energy: 8, stress: 7 },
      { weight: 77.0, adherence: 95, weekend: 91, hunger: 8, energy: 7, stress: 8 },
    ]
  },
  on_track: {
    name: "On Track (0.5-1%/week)",
    description: "Ideal weight loss pace",
    checkins: [
      { weight: 80.0, adherence: 88, weekend: 82, hunger: 7, energy: 8, stress: 7 },
      { weight: 79.4, adherence: 90, weekend: 85, hunger: 8, energy: 8, stress: 8 },
      { weight: 78.9, adherence: 87, weekend: 80, hunger: 7, energy: 7, stress: 7 },
      { weight: 78.4, adherence: 89, weekend: 84, hunger: 8, energy: 8, stress: 8 },
    ]
  },
  high_hunger: {
    name: "High Hunger (2+ weeks)",
    description: "Good adherence but persistently hungry",
    checkins: [
      { weight: 80.0, adherence: 88, weekend: 82, hunger: 3, energy: 7, stress: 7 },
      { weight: 79.5, adherence: 90, weekend: 85, hunger: 2, energy: 6, stress: 7 },
      { weight: 79.0, adherence: 87, weekend: 80, hunger: 2, energy: 6, stress: 6 },
      { weight: 78.6, adherence: 89, weekend: 84, hunger: 3, energy: 7, stress: 7 },
    ]
  },
  low_energy: {
    name: "Low Energy (2+ weeks)",
    description: "Fatigue despite good adherence",
    checkins: [
      { weight: 80.0, adherence: 85, weekend: 78, hunger: 7, energy: 3, stress: 6 },
      { weight: 79.6, adherence: 87, weekend: 80, hunger: 6, energy: 2, stress: 6 },
      { weight: 79.2, adherence: 84, weekend: 75, hunger: 7, energy: 2, stress: 5 },
      { weight: 78.8, adherence: 86, weekend: 79, hunger: 7, energy: 3, stress: 6 },
    ]
  },
  low_adherence: {
    name: "Low Adherence (<60%)",
    description: "Struggling with consistency",
    checkins: [
      { weight: 80.0, adherence: 45, weekend: 30, hunger: 5, energy: 5, stress: 4 },
      { weight: 80.2, adherence: 50, weekend: 35, hunger: 4, energy: 5, stress: 5 },
      { weight: 79.8, adherence: 48, weekend: 32, hunger: 5, energy: 4, stress: 4 },
      { weight: 80.0, adherence: 52, weekend: 38, hunger: 5, energy: 5, stress: 5 },
    ]
  },
  weekend_struggle: {
    name: "Weekend Struggle",
    description: "Great weekdays, poor weekends",
    checkins: [
      { weight: 80.0, adherence: 82, weekend: 40, hunger: 6, energy: 7, stress: 6 },
      { weight: 79.8, adherence: 80, weekend: 35, hunger: 7, energy: 7, stress: 7 },
      { weight: 79.9, adherence: 83, weekend: 42, hunger: 6, energy: 6, stress: 6 },
      { weight: 79.7, adherence: 81, weekend: 38, hunger: 7, energy: 7, stress: 7 },
    ]
  }
};

export default function TestCheckinManager() {
  const navigate = useNavigate();
  const [testClients, setTestClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [existingCheckins, setExistingCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('coach_token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch test clients on mount
  useEffect(() => {
    fetchTestClients();
  }, []);

  const fetchTestClients = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/test-clients-list`, { headers: getAuthHeaders() });
      setTestClients(res.data.test_clients || []);
    } catch (err) {
      setError('Failed to load test clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientCheckins = async (clientId) => {
    try {
      const res = await axios.get(`${API}/admin/test-client-checkins/${clientId}`, { headers: getAuthHeaders() });
      setExistingCheckins(res.data.checkins || []);
    } catch (err) {
      setExistingCheckins([]);
    }
  };

  const handleClientSelect = (clientId) => {
    const client = testClients.find(c => c.id === clientId);
    setSelectedClient(client);
    setCheckins([]);
    setSuccess(null);
    setError(null);
    if (client) {
      fetchClientCheckins(clientId);
    }
  };

  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;

    // Generate dates starting from 4 weeks ago
    const today = new Date();
    const newCheckins = preset.checkins.map((c, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (preset.checkins.length - 1 - i) * 7);
      return {
        id: Date.now() + i,
        week_date: date.toISOString().split('T')[0],
        weight_kg: c.weight,
        overall_adherence: c.adherence,
        weekend_adherence: c.weekend,
        hunger: c.hunger,
        energy: c.energy,
        stress: c.stress,
        notes: ''
      };
    });

    setCheckins(newCheckins);
    setSuccess(`Applied "${preset.name}" preset with ${newCheckins.length} check-ins`);
  };

  const addEmptyCheckin = () => {
    const lastDate = checkins.length > 0 
      ? new Date(checkins[checkins.length - 1].week_date)
      : new Date();
    
    const newDate = new Date(lastDate);
    newDate.setDate(newDate.getDate() + 7);

    setCheckins([...checkins, {
      id: Date.now(),
      week_date: newDate.toISOString().split('T')[0],
      weight_kg: 80.0,
      overall_adherence: 85,
      weekend_adherence: 75,
      hunger: 7,
      energy: 7,
      stress: 7,
      notes: ''
    }]);
  };

  const updateCheckin = (id, field, value) => {
    setCheckins(checkins.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const removeCheckin = (id) => {
    setCheckins(checkins.filter(c => c.id !== id));
  };

  const saveCheckins = async (clearExisting = false) => {
    if (!selectedClient || checkins.length === 0) {
      setError('Select a client and add at least one check-in');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        client_id: selectedClient.id,
        checkins: checkins.map(c => ({
          week_date: c.week_date,
          weight_kg: parseFloat(c.weight_kg),
          overall_adherence: parseInt(c.overall_adherence),
          weekend_adherence: parseInt(c.weekend_adherence),
          hunger: parseInt(c.hunger),
          energy: parseInt(c.energy),
          stress: parseInt(c.stress),
          notes: c.notes || null
        })),
        clear_existing: clearExisting
      };

      const res = await axios.post(`${API}/admin/test-client-checkins`, payload, { headers: getAuthHeaders() });
      setSuccess(`Created ${res.data.created_checkins} check-ins successfully!`);
      fetchClientCheckins(selectedClient.id);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save check-ins');
    } finally {
      setSaving(false);
    }
  };

  const clearClientData = async () => {
    if (!selectedClient) return;
    if (!window.confirm(`Delete all check-ins and adjustment data for ${selectedClient.first_name} ${selectedClient.last_name}?`)) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/admin/test-client-checkins/${selectedClient.id}`, { headers: getAuthHeaders() });
      setSuccess('All check-in data cleared');
      setExistingCheckins([]);
    } catch (err) {
      setError('Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/coach/clients')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Test Check-in Manager</h1>
            <p className="text-gray-500">Create backdated check-ins to test adjustment scenarios</p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Client Selection & Presets */}
          <div className="space-y-4">
            {/* Client Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Select Test Client</CardTitle>
              </CardHeader>
              <CardContent>
                <Select onValueChange={handleClientSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a test client..." />
                  </SelectTrigger>
                  <SelectContent>
                    {testClients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.first_name} {client.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedClient && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg text-sm">
                    <p><strong>Email:</strong> {selectedClient.email}</p>
                    <p><strong>Existing check-ins:</strong> {existingCheckins.length}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Presets */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Presets</CardTitle>
                <CardDescription>Load a scenario template</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(PRESETS).map(([key, preset]) => (
                  <Button
                    key={key}
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-2"
                    onClick={() => applyPreset(key)}
                    disabled={!selectedClient}
                  >
                    <div>
                      <div className="font-medium">{preset.name}</div>
                      <div className="text-xs text-gray-500">{preset.description}</div>
                    </div>
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Existing Check-ins */}
            {existingCheckins.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Existing Check-ins</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {existingCheckins.map(c => (
                      <div key={c.id} className="text-sm p-2 bg-gray-50 rounded">
                        <div className="font-medium">Week {c.week_number}</div>
                        <div className="text-gray-500">
                          {c.current_weight_kg}kg | {c.overall_adherence}% adh
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="w-full mt-3"
                    onClick={clearClientData}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All Data
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Check-in Editor */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Check-ins to Create</CardTitle>
                  <CardDescription>
                    {checkins.length} check-in{checkins.length !== 1 ? 's' : ''} queued
                  </CardDescription>
                </div>
                <Button onClick={addEmptyCheckin} disabled={!selectedClient}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Week
                </Button>
              </CardHeader>
              <CardContent>
                {checkins.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>No check-ins yet.</p>
                    <p className="text-sm mt-1">Select a preset or add weeks manually.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {checkins.map((checkin, index) => (
                      <div key={checkin.id} className="p-4 border rounded-lg bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-medium">Week {index + 1}</h4>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCheckin(checkin.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {/* Date */}
                          <div>
                            <Label className="text-xs">Date</Label>
                            <Input
                              type="date"
                              value={checkin.week_date}
                              onChange={(e) => updateCheckin(checkin.id, 'week_date', e.target.value)}
                            />
                          </div>

                          {/* Weight */}
                          <div>
                            <Label className="text-xs">Weight (kg)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={checkin.weight_kg}
                              onChange={(e) => updateCheckin(checkin.id, 'weight_kg', e.target.value)}
                            />
                          </div>

                          {/* Overall Adherence */}
                          <div>
                            <Label className="text-xs">Adherence %</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={checkin.overall_adherence}
                              onChange={(e) => updateCheckin(checkin.id, 'overall_adherence', e.target.value)}
                            />
                          </div>

                          {/* Weekend Adherence */}
                          <div>
                            <Label className="text-xs">Weekend %</Label>
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={checkin.weekend_adherence}
                              onChange={(e) => updateCheckin(checkin.id, 'weekend_adherence', e.target.value)}
                            />
                          </div>
                        </div>

                        {/* Comfort Scores */}
                        <div className="grid grid-cols-3 gap-4 mt-3">
                          <div>
                            <Label className="text-xs">Hunger (10=satisfied)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="10"
                                value={checkin.hunger}
                                onChange={(e) => updateCheckin(checkin.id, 'hunger', e.target.value)}
                                className="w-16"
                              />
                              <span className="text-xs text-gray-500">
                                {checkin.hunger <= 3 ? '🔴 Hungry' : checkin.hunger >= 7 ? '🟢 Good' : '🟡 OK'}
                              </span>
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Energy (10=energized)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="10"
                                value={checkin.energy}
                                onChange={(e) => updateCheckin(checkin.id, 'energy', e.target.value)}
                                className="w-16"
                              />
                              <span className="text-xs text-gray-500">
                                {checkin.energy <= 3 ? '🔴 Tired' : checkin.energy >= 7 ? '🟢 Good' : '🟡 OK'}
                              </span>
                            </div>
                          </div>

                          <div>
                            <Label className="text-xs">Stress (10=calm)</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="10"
                                value={checkin.stress}
                                onChange={(e) => updateCheckin(checkin.id, 'stress', e.target.value)}
                                className="w-16"
                              />
                              <span className="text-xs text-gray-500">
                                {checkin.stress <= 3 ? '🔴 Stressed' : checkin.stress >= 7 ? '🟢 Good' : '🟡 OK'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Save Buttons */}
                {checkins.length > 0 && (
                  <div className="flex gap-3 mt-6">
                    <Button
                      onClick={() => saveCheckins(false)}
                      disabled={saving || !selectedClient}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Add to Existing'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => saveCheckins(true)}
                      disabled={saving || !selectedClient}
                      className="flex-1"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Replace All
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="mt-4">
              <CardContent className="pt-4">
                <h4 className="font-medium mb-2">How to Use</h4>
                <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                  <li>Select a test client from the dropdown</li>
                  <li>Choose a preset scenario OR add weeks manually</li>
                  <li>Adjust the values to match your test case</li>
                  <li>Click &quot;Add to Existing&quot; or &quot;Replace All&quot; to save</li>
                  <li>Go to Coach Adjustments to see the bucket assignment</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
